import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Camera, Image as ImageIcon, Lock, Pencil, Trash2, Check, Palette, FolderPlus, Share2, X, Scissors, Star } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { THEME_PRESETS, DEFAULT_THEME, themeGradient } from "../lib/themes";
import Navbar from "../components/Navbar";
import Avatar from "../components/social/Avatar";
import MediaUpload from "../components/social/MediaUpload";
import TrimmedVideo from "../components/social/TrimmedVideo";
import TrimDialog from "../components/social/TrimDialog";
import PostCard from "../components/social/PostCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

export default function MyProfile() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [me, setMe] = useState(null);
  const [posts, setPosts] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [activeAlbum, setActiveAlbum] = useState("all");
  const [newAlbum, setNewAlbum] = useState("");
  const [addingAlbum, setAddingAlbum] = useState(false);
  const [trimItem, setTrimItem] = useState(null);
  const [editingBio, setEditingBio] = useState(false);
  const [bio, setBio] = useState("");
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [busy, setBusy] = useState("");
  const coverRef = useRef(null);
  const picRef = useRef(null);

  const load = useCallback(async () => {
    const [meR, gR, aR, pR] = await Promise.all([
      api.get("/social/me"), api.get("/social/gallery"), api.get("/social/albums"), api.get("/posts"),
    ]);
    setMe(meR.data);
    setBio(meR.data.bio || "");
    setTheme(meR.data.theme || DEFAULT_THEME);
    setGallery(gR.data);
    setAlbums(aR.data);
    setPosts(pR.data.filter((p) => p.author_user_id === meR.data.user_id));
  }, []);

  useEffect(() => { load(); }, [load]);

  const patchMe = async (payload) => {
    const { data } = await api.put("/social/me", payload);
    setMe((prev) => ({ ...prev, ...data }));
    checkAuth();
    return data;
  };

  const uploadImage = async (file, field) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Image must be under 8MB"); return; }
    setBusy(field);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd);
      await patchMe({ [field]: data.url });
      toast.success(field === "cover" ? "Banner updated 🎨" : "Profile picture updated 😊");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy("");
    }
  };

  const saveBio = async () => {
    await patchMe({ bio });
    setEditingBio(false);
    toast.success("Bio saved 💛");
  };

  const applyTheme = async (t) => {
    setTheme(t);
    await patchMe({ theme: t });
    toast.success("Theme applied ✨");
  };

  const addMedia = async ({ url, type }) => {
    const album_id = activeAlbum === "all" ? null : activeAlbum;
    const { data } = await api.post("/social/gallery", { type, url, album_id });
    setGallery((g) => [data, ...g]);
  };

  const deleteMedia = async (id) => {
    await api.delete(`/social/gallery/${id}`);
    setGallery((g) => g.filter((i) => i.id !== id));
    toast.success("Removed");
  };

  const createAlbum = async () => {
    const name = newAlbum.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/social/albums", { name });
      setAlbums((a) => [...a, data]);
      setNewAlbum(""); setAddingAlbum(false); setActiveAlbum(data.id);
      toast.success(`Album "${data.name}" created 📁`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteAlbum = async (id) => {
    await api.delete(`/social/albums/${id}`);
    setAlbums((a) => a.filter((x) => x.id !== id));
    setGallery((g) => g.map((i) => (i.album_id === id ? { ...i, album_id: null } : i)));
    if (activeAlbum === id) setActiveAlbum("all");
    toast.success("Album deleted (photos kept)");
  };

  const moveItem = async (id, album_id) => {
    await api.put(`/social/gallery/${id}`, { album_id: album_id || null, set_album: true });
    setGallery((g) => g.map((i) => (i.id === id ? { ...i, album_id: album_id || null } : i)));
  };

  const saveTrim = async ({ trim_start, trim_end }) => {
    if (!trimItem) return;
    await api.put(`/social/gallery/${trimItem.id}`, { trim_start, trim_end, set_album: false });
    setGallery((g) => g.map((i) => (i.id === trimItem.id ? { ...i, trim_start, trim_end } : i)));
    toast.success("Clip trimmed ✂️");
  };

  const setAlbumCover = async (albumId, url) => {
    const { data } = await api.put(`/social/albums/${albumId}`, { cover_url: url });
    setAlbums((a) => a.map((x) => (x.id === albumId ? { ...x, cover_url: data.cover_url || url } : x)));
    toast.success("Album cover set 📔");
  };

  const shareToFeed = async (item) => {
    try {
      const payload = item.type === "video"
        ? { text: item.caption || "", video: item.url, trim_start: item.trim_start, trim_end: item.trim_end }
        : { text: item.caption || "", image: item.url };
      const { data } = await api.post("/posts", payload);
      setPosts((ps) => [{ ...data, author_user_id: me.user_id }, ...ps]);
      toast.success("Shared to Playground 🎈");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const likePost = async (post) => {
    const { data } = await api.post(`/posts/${post.post_id}/like`);
    setPosts((ps) => ps.map((p) => p.post_id === post.post_id ? { ...p, likes: data.likes } : p));
  };
  const commentPost = async (post, txt) => {
    const { data } = await api.post(`/posts/${post.post_id}/comment`, { text: txt });
    setPosts((ps) => ps.map((p) => p.post_id === post.post_id ? { ...p, comments: [...(p.comments || []), data] } : p));
  };
  const reactPost = async (post, sticker) => {
    const { data } = await api.post(`/posts/${post.post_id}/react`, { sticker });
    setPosts((ps) => ps.map((p) => p.post_id === post.post_id ? { ...p, reactions: data.reactions } : p));
  };
  const deletePost = async (post) => {
    await api.delete(`/posts/${post.post_id}`);
    setPosts((ps) => ps.filter((p) => p.post_id !== post.post_id));
    toast.success("Post deleted");
  };
  const pinPost = async (post) => {
    const { data } = await api.post(`/posts/${post.post_id}/pin`);
    setPosts((ps) => ps.map((p) => ({ ...p, pinned: p.post_id === post.post_id ? data.pinned : false })));
    toast.success(data.pinned ? "Pinned to your profile 📌" : "Unpinned");
  };

  if (!me) return <div className="min-h-screen bg-[#FFFDF7]"><Navbar /><p className="text-center text-slate-400 py-16">Loading your profile…</p></div>;

  const t = theme || DEFAULT_THEME;

  return (
    <div className="min-h-screen pb-16 transition-colors" style={{ backgroundColor: t.bg }}>
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <button onClick={() => navigate("/social")} data-testid="back-to-playground-btn"
          className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-800 font-semibold text-sm mb-4">
          <ArrowLeft className="h-4 w-4" /> Back to Playground
        </button>

        {/* Header card */}
        <div className="bg-white rounded-3xl shadow-lg overflow-hidden" data-testid="myprofile-header">
          <div className="h-40 sm:h-48 relative" style={me.cover ? { backgroundImage: `url(${me.cover})`, backgroundSize: "cover", backgroundPosition: "center" } : { background: themeGradient(t) }}>
            <input ref={coverRef} type="file" accept="image/*" className="hidden" data-testid="cover-input"
              onChange={(e) => uploadImage(e.target.files?.[0], "cover")} />
            <button onClick={() => coverRef.current?.click()} disabled={busy === "cover"} data-testid="change-banner-btn"
              className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-black/55 text-white px-3 py-2 text-sm font-semibold hover:bg-black/75 backdrop-blur">
              <ImageIcon className="h-4 w-4" /> {busy === "cover" ? "Uploading…" : "Change banner"}
            </button>
          </div>
          <div className="px-6 pb-6 -mt-12">
            <div className="relative inline-block">
              <Avatar user={me} size={96} className="ring-4 ring-white" testid="myprofile-avatar" />
              <input ref={picRef} type="file" accept="image/*" className="hidden" data-testid="picture-input"
                onChange={(e) => uploadImage(e.target.files?.[0], "picture")} />
              <button onClick={() => picRef.current?.click()} disabled={busy === "picture"} data-testid="change-picture-btn"
                className="absolute bottom-1 right-1 h-9 w-9 rounded-full text-white flex items-center justify-center shadow-md hover:opacity-90"
                style={{ backgroundColor: t.primary }} title="Change profile picture">
                <Camera className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-3 flex items-start gap-3">
              <div className="flex-1">
                <h1 className="font-display font-bold text-2xl text-slate-900" data-testid="myprofile-name">{me.name}</h1>
                <div className="flex gap-4 mt-1 text-sm">
                  <span className="text-slate-600"><b className="text-slate-900">{me.friends_count ?? 0}</b> friends</span>
                  <span className="text-slate-600"><b className="text-slate-900">{me.posts_count ?? 0}</b> posts</span>
                </div>
              </div>
            </div>

            {/* Bio */}
            <div className="mt-4">
              {editingBio ? (
                <div>
                  <Textarea data-testid="myprofile-bio-input" value={bio} maxLength={300} onChange={(e) => setBio(e.target.value)}
                    placeholder="Tell families a little about you…" className="rounded-2xl min-h-[72px]" />
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" onClick={saveBio} data-testid="save-bio-btn" className="rounded-full font-bold" style={{ backgroundColor: t.primary }}>
                      <Check className="h-4 w-4 mr-1" /> Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setBio(me.bio || ""); setEditingBio(false); }} className="rounded-full">Cancel</Button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setEditingBio(true)} data-testid="edit-bio-btn"
                  className="group flex items-start gap-2 text-left w-full">
                  <span className={me.bio ? "text-slate-600" : "text-slate-400 italic"}>{me.bio || "Add a short bio…"}</span>
                  <Pencil className="h-4 w-4 text-slate-300 group-hover:text-slate-500 shrink-0 mt-0.5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* My posts */}
        <div className="mt-6" data-testid="myprofile-posts">
          <h2 className="font-display font-bold text-slate-800 mb-4 px-1">My posts</h2>
          {posts.length === 0 ? (
            <div className="bg-white rounded-3xl shadow-lg p-10 text-center text-slate-400">
              You haven't posted yet. Share a moment from the Playground or your gallery! 🎈
            </div>
          ) : (
            <div className="space-y-6">
              {[...posts].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0)).map((p) => (
                <PostCard key={p.post_id} post={p} user={user} onLike={likePost} onComment={commentPost} onReact={reactPost} onPin={pinPost} onDelete={deletePost} />
              ))}
            </div>
          )}
        </div>

        {/* Theme picker */}
        <div className="bg-white rounded-3xl shadow-lg p-6 mt-6" data-testid="theme-picker">
          <div className="flex items-center gap-2 mb-4">
            <Palette className="h-5 w-5" style={{ color: t.primary }} />
            <h2 className="font-display font-bold text-slate-800">Colour theme</h2>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {THEME_PRESETS.map((p) => {
              const active = t.name === p.name;
              return (
                <button key={p.name} onClick={() => applyTheme(p)} data-testid={`theme-${p.name}`}
                  className={`rounded-2xl p-1 border-2 transition ${active ? "border-slate-800" : "border-transparent hover:border-slate-200"}`}>
                  <div className="h-12 rounded-xl" style={{ background: themeGradient(p) }} />
                  <div className="text-xs font-semibold text-slate-600 mt-1 text-center">{p.label}</div>
                </button>
              );
            })}
          </div>

          {/* Custom colours */}
          <div className="mt-5 pt-5 border-t border-slate-100">
            <div className="text-sm font-bold text-slate-700 mb-3">Or pick your own colours</div>
            <div className="flex flex-wrap items-end gap-4">
              {[["primary", "Main"], ["accent", "Accent"], ["bg", "Background"]].map(([key, label]) => (
                <label key={key} className="flex flex-col items-center gap-1">
                  <input type="color" value={t[key]} data-testid={`custom-${key}`}
                    onChange={(e) => setTheme((prev) => ({ ...prev, name: "custom", [key]: e.target.value }))}
                    className="h-11 w-14 rounded-lg border border-slate-200 cursor-pointer bg-white" />
                  <span className="text-xs text-slate-500">{label}</span>
                </label>
              ))}
              <Button onClick={() => applyTheme({ ...theme, name: "custom" })} data-testid="apply-custom-theme-btn"
                className="rounded-full font-bold ml-auto" style={{ backgroundColor: t.primary }}>
                Apply custom
              </Button>
            </div>
          </div>
        </div>

        {/* Gallery */}
        <div className="bg-white rounded-3xl shadow-lg p-6 mt-6" data-testid="my-gallery">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="font-display font-bold text-slate-800">My gallery</h2>
            <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-slate-400">
              <Lock className="h-3.5 w-3.5" /> Private to you
            </span>
          </div>
          <p className="text-sm text-slate-400 mb-4">Upload your own photos and short video clips, and group them into albums. Only you can see these.</p>

          {/* Album chips */}
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="album-chips">
            {[{ id: "all", name: "All" }, ...albums].map((al) => {
              const active = activeAlbum === al.id;
              const count = al.id === "all" ? gallery.length : gallery.filter((i) => i.album_id === al.id).length;
              return (
                <span key={al.id} className={`group inline-flex items-center gap-1 rounded-full pl-1.5 pr-2 py-1 text-sm font-semibold border-2 transition ${active ? "text-white border-transparent" : "bg-slate-50 text-slate-600 border-slate-100 hover:border-slate-200"}`}
                  style={active ? { backgroundColor: t.primary } : {}}>
                  {al.id !== "all" && (
                    al.cover_url
                      ? <img src={al.cover_url} alt="" className="h-6 w-6 rounded-full object-cover ring-1 ring-white/50" data-testid={`album-cover-${al.id}`} />
                      : <span className="h-6 w-6 rounded-full flex items-center justify-center text-[10px]" style={{ background: themeGradient(t) }}>📁</span>
                  )}
                  <button onClick={() => setActiveAlbum(al.id)} className="px-1" data-testid={`album-chip-${al.id}`}>{al.name} <span className="opacity-70">({count})</span></button>
                  {al.id !== "all" && (
                    <button onClick={() => deleteAlbum(al.id)} data-testid={`delete-album-${al.id}`} title="Delete album"
                      className={`rounded-full p-0.5 ${active ? "hover:bg-white/20" : "hover:bg-slate-200"}`}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </span>
              );
            })}
            {addingAlbum ? (
              <span className="inline-flex items-center gap-1">
                <Input autoFocus value={newAlbum} onChange={(e) => setNewAlbum(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && createAlbum()} placeholder="Album name"
                  className="h-9 w-36 rounded-full text-sm" data-testid="new-album-input" />
                <Button size="sm" onClick={createAlbum} data-testid="save-album-btn" className="rounded-full h-9" style={{ backgroundColor: t.primary }}>Add</Button>
                <Button size="sm" variant="ghost" onClick={() => { setAddingAlbum(false); setNewAlbum(""); }} className="rounded-full h-9">Cancel</Button>
              </span>
            ) : (
              <button onClick={() => setAddingAlbum(true)} data-testid="add-album-btn"
                className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-semibold text-slate-500 border-2 border-dashed border-slate-200 hover:border-[#FF5A79] hover:text-[#FF5A79]">
                <FolderPlus className="h-4 w-4" /> New album
              </button>
            )}
          </div>

          <MediaUpload onUploaded={addMedia} testid="gallery-upload" />

          {(() => {
            const items = activeAlbum === "all" ? gallery : gallery.filter((i) => i.album_id === activeAlbum);
            if (items.length === 0) return null;
            return (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-5" data-testid="gallery-grid">
                {items.map((item) => (
                  <div key={item.id} className="relative group rounded-2xl overflow-hidden bg-slate-100" data-testid={`gallery-item-${item.id}`}>
                    <div className="aspect-square">
                      {item.type === "video" ? (
                        <TrimmedVideo src={item.url} start={item.trim_start} end={item.trim_end} className="w-full h-full object-cover" />
                      ) : (
                        <img src={item.url} alt={item.caption || "memory"} className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition">
                      {item.type === "video" && (
                        <button onClick={() => setTrimItem(item)} data-testid={`trim-media-${item.id}`} title="Trim clip"
                          className="bg-black/60 text-white rounded-full p-1.5 hover:bg-[#FF5A79]">
                          <Scissors className="h-4 w-4" />
                        </button>
                      )}
                      {activeAlbum !== "all" && (
                        <button onClick={() => setAlbumCover(activeAlbum, item.url)} data-testid={`cover-media-${item.id}`} title="Set as album cover"
                          className="bg-black/60 text-white rounded-full p-1.5 hover:bg-amber-500">
                          <Star className="h-4 w-4" />
                        </button>
                      )}
                      <button onClick={() => shareToFeed(item)} data-testid={`share-media-${item.id}`} title="Share to Playground"
                        className="bg-black/60 text-white rounded-full p-1.5 hover:bg-[#2AB7CA]">
                        <Share2 className="h-4 w-4" />
                      </button>
                      <button onClick={() => deleteMedia(item.id)} data-testid={`delete-media-${item.id}`} title="Delete"
                        className="bg-black/60 text-white rounded-full p-1.5 hover:bg-red-500">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    {albums.length > 0 && (
                      <select value={item.album_id || ""} onChange={(e) => moveItem(item.id, e.target.value)}
                        data-testid={`move-media-${item.id}`}
                        className="absolute bottom-2 left-2 right-2 rounded-lg bg-black/60 text-white text-xs px-2 py-1 border-0 opacity-0 group-hover:opacity-100 transition">
                        <option value="">No album</option>
                        {albums.map((al) => <option key={al.id} value={al.id}>{al.name}</option>)}
                      </select>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </div>
      <TrimDialog open={!!trimItem} onClose={() => setTrimItem(null)} item={trimItem} onSave={saveTrim} />
    </div>
  );
}
